import {
  getFieldCrops,
  getFirstInstallationDate,
} from '../../managers/distributionManager';
import {
  getRecommendation,
  isSupportingPlannerByWeeks,
} from '../../managers/fieldManager';
const { plannerType } = require('@managers/planningManager');

var distributionActions = require('@actions/distributionActions');
var {
  setNetworkError,
  clearNetworkError,
} = require('@actions/networkErrorActions');
var distributionManager = require('@managers/distributionManager');
var farmingUnitManager = require('@managers/farmingUnitManager');
var fieldManager = require('@managers/fieldManager');
var sensorsManager = require('@managers/sensorsManager');
var GeneralClasses = require('@model/GeneralClasses');
var { error_message } = require('@managers/helpers');
var {
  getItem,
  setItem,
  removeItem,
} = require('@managers/localStorageManager.jsx');
var { getMixpanelInstance } = require('@managers/mixpanelManager');
var {
  preserveDistributionClientData,
} = require('../lib/preserveDistributionClientData');

// historical_data (crop water availability) is real-time and must never be
// persisted — strip it at every level when serializing distributions for the
// cache. A JSON.stringify replacer does this in one pass without cloning or
// mutating the in-memory tree (which keeps historical_data for the session).
var stringifyDistributionsForPersist = data =>
  JSON.stringify(data, (key, value) =>
    key === 'historical_data' ? undefined : value
  );

// Monotonic request id for load_distributions. Guards against stale responses
// from earlier requests overwriting newer field updates.
let lastLoadDistributionsRequestId = 0;
// While true, selected_entities skips history so farming_unit/field history does
// not compete with the priority GET distribution/{id} on the API connection pool.
let deferHistoryDuringInitialTreeLoad = false;

// In-flight dedup for load_distribution_by_id — concurrent callers (e.g.
// notifications filter + notification click) share one GET per distributionId.
// AbortControllers let the background distributions(1,5) bulk win: if bulk
// lands a tree first, the matching per-id request is cancelled. We never abort
// the bulk — pages batch many dists into one response.
const pendingDistributionTreeLoads = {};
const pendingDistributionTreeAbortControllers = {};

// In-flight dedup for get_field_history — selected_entities (cache + by-id)
// and IrrigationSystemMonitoring can all request the same field in one tick.
const pendingFieldHistoryLoads = {};

// Same for get_farming_units_history — selected_entities ×2 + AllFarmView effect.
const pendingFarmingUnitHistoryLoads = {};

// Same for get_farming_units_recommendations — each run hits
// field/recommendations/{fieldId} per weekly field.
const pendingFarmingUnitRecommendationsLoads = {};

// Singleflight for ensure_all_distributions_loaded so startup backfill and a
// cross-dist screen open share one distributions(1,5) pass. pendingEnsureAllForce
// tracks whether the in-flight pass is a forced revalidate; a later force while
// a non-force is running chains a second forced pass so startup cannot be dropped.
let pendingEnsureAllPromise = null;
let pendingEnsureAllForce = false;

const isRequestAborted = err =>
  !!err &&
  (err.code === 'ERR_CANCELED' ||
    err.name === 'CanceledError' ||
    err.name === 'AbortError');

const abortPendingDistributionTreeLoad = distributionId => {
  const controller = pendingDistributionTreeAbortControllers[distributionId];
  if (controller) {
    controller.abort();
    delete pendingDistributionTreeAbortControllers[distributionId];
  }
  // Leave pendingDistributionTreeLoads so awaiters still resolve through the
  // aborted promise's catch (which reads the bulk-merged tree from state).
};

const DISTRIBUTIONS_CACHE_KEY = 'DISTRIBUTIONS_DATA';

// Cache freshness. parseDistributionsCache guards on schema version only, so a
// tree hydrated once would otherwise live in IndexedDB forever. We keep serving
// cached trees instantly, but once a tree's last network (or cache-paint) time
// is older than this we revalidate in the background (stale-while-revalidate).
// Per-id timestamps so TTL re-arms within a session — a one-shot "fresh this
// session" set would permanently suppress refetch after the first ensure_all.
const DISTRIBUTIONS_CACHE_STALE_MS = 60 * 60 * 1000; // 1h

// Startup ensure_all after first paint. When true, refreshes every full tree
// even inside TTL (old "reload = refetch everything" behavior). When false
// (current default), only missing skeletons + TTL-stale trees hit the bulk
// route — cheaper for large/root accounts. Flip to true if product wants
// hard refresh on every app/web reload.
const FORCE_REVALIDATE_DISTRIBUTIONS_ON_RELOAD = false;

// Last time each distribution tree was considered fresh: network merge uses
// Date.now(); cache paint seeds from the blob's savedAt so cold start can
// skip/by-id-SWR correctly.
const distributionsFreshAt = new Map();

const markDistributionTreeFresh = (distributionId, at = Date.now()) => {
  if (distributionId) {
    distributionsFreshAt.set(distributionId, at);
  }
};

const seedFreshAtFromCache = cache => {
  const savedAt = cache?.savedAt;
  if (savedAt == null || !cache?.distributions) {
    return;
  }
  for (const id of Object.keys(cache.distributions)) {
    // Only seed if we have no newer in-memory mark (e.g. HMR / overlapping load).
    if (!distributionsFreshAt.has(id)) {
      distributionsFreshAt.set(id, savedAt);
    }
  }
};

const isFullDistributionTreeStale = distributionId => {
  const freshAt = distributionsFreshAt.get(distributionId);
  if (freshAt == null) {
    // Full tree with no timestamp (shouldn't happen after paint/seed) — refresh.
    return true;
  }
  return Date.now() - freshAt > DISTRIBUTIONS_CACHE_STALE_MS;
};

// Alias kept for call sites / readability — see preserveDistributionClientData.
const preserveHistoricalData = preserveDistributionClientData;

// Derive the lightweight [{id, name}] switcher index from a distributions map,
// sorted by name (tie-break id) to match the backend skeleton ordering. The
// cache no longer stores a separate index — it is derived from the data, and
// there is no separate Redux state slice for it either (see
// selectDistributionsIndex below for the memoized version consumers use).
export const deriveIndexFromDistributions = distributionsMap => {
  if (!distributionsMap) {
    return [];
  }
  return Object.values(distributionsMap)
    .filter(d => d?.id)
    .map(d => ({ id: d.id, name: d.name || '' }))
    .sort(
      (a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase()) ||
        a.id.localeCompare(b.id)
    );
};

// Single-slot memoized selector for the switcher index. Consumers (dropdowns,
// entity selectors) call this from mapStateToProps/useSelector instead of
// reading a separately-dispatched Redux slice — recomputes only when the
// distributions map reference actually changes, so it stays a stable
// reference across unrelated re-renders (avoids extra renders from a fresh
// array on every store update).
let _distributionsIndexMemoInput = null;
let _distributionsIndexMemoOutput = [];
export const selectDistributionsIndex = distributionsMap => {
  if (distributionsMap === _distributionsIndexMemoInput) {
    return _distributionsIndexMemoOutput;
  }
  _distributionsIndexMemoInput = distributionsMap;
  _distributionsIndexMemoOutput =
    deriveIndexFromDistributions(distributionsMap);
  return _distributionsIndexMemoOutput;
};

const parseDistributionsCache = raw => {
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw);
    // Schema guard (version, not TTL). Only the distributions map is required;
    // the switcher index is derived from it on read.
    if (
      parsed?.version === 2 &&
      parsed.distributions &&
      Object.keys(parsed.distributions).length
    ) {
      return parsed;
    }
  } catch (e) {
    // Best-effort: unexpected shape → ignore and load from server
  }
  return null;
};

const stringifyDistributionsCache = cachePayload =>
  stringifyDistributionsForPersist(cachePayload);

const resolveCurrentDistributionId = async getState => {
  const state = getState();
  const pending = state.distribution_data.pending_push_selection;
  if (pending?.distribution_id) {
    return pending.distribution_id;
  }

  if (typeof window !== 'undefined' && window.location) {
    const urlParams = new URLSearchParams(window.location.search);
    const distributionId = urlParams.get('distributionId');
    if (distributionId) {
      return distributionId;
    }
  }

  const cachedId = await getItem('SELECTED_DISTRIBUTION_ID', null);
  return cachedId || null;
};

// Resolve selected distribution id as early as possible for the t=0 by-id fetch.
const resolveSelectedDistIdForFetch = async (
  getState,
  selectedDistributionId,
  cacheReadPromise,
  shouldUseCache
) => {
  if (selectedDistributionId) {
    return selectedDistributionId;
  }

  const state = getState();
  const fromEntities =
    state.distribution_data.selected_entities.distribution?.id;
  if (fromEntities) {
    return fromEntities;
  }

  const pending = state.distribution_data.pending_push_selection;
  if (pending?.distribution_id) {
    return pending.distribution_id;
  }

  if (typeof window !== 'undefined' && window.location) {
    const urlParams = new URLSearchParams(window.location.search);
    const distributionId = urlParams.get('distributionId');
    if (distributionId) {
      return distributionId;
    }
  }

  const cachedId = await getItem('SELECTED_DISTRIBUTION_ID', null);
  if (cachedId) {
    return cachedId;
  }

  if (shouldUseCache) {
    const raw = await cacheReadPromise;
    const cache = parseDistributionsCache(raw);
    if (cache?.currentId) {
      return cache.currentId;
    }
  }

  return null;
};

const deriveSelectedDistIdFromState = (
  state,
  selectedDistributionId,
  cache,
  currentId
) => {
  const distributionsMap =
    state.distribution_data.distributions?.distributions || {};
  return (
    selectedDistributionId ||
    state.distribution_data.selected_entities.distribution?.id ||
    (cache?.currentId && cache.currentId in distributionsMap
      ? cache.currentId
      : null) ||
    (currentId && currentId in distributionsMap ? currentId : null) ||
    deriveIndexFromDistributions(distributionsMap)[0]?.id ||
    null
  );
};

const mergeSelectedDistributionTree = (
  dispatch,
  getState,
  requestId,
  selectedDistId,
  tree
) => {
  if (
    requestId !== lastLoadDistributionsRequestId ||
    !tree ||
    !selectedDistId
  ) {
    return false;
  }

  const merged = mergeDistributionsResult(getState(), {
    distributions: { [selectedDistId]: tree },
  });
  dispatch(set_all_distributions(merged));
  markDistributionTreeFresh(selectedDistId);

  deferHistoryDuringInitialTreeLoad = false;

  const selectedDist = merged.distributions?.[selectedDistId];
  if (distributionManager.isFullDistributionTree(selectedDist)) {
    dispatch(distributionActions.selected_entities(null, null, null, null));
  }

  schedulePersistDistributionsCache(getState, selectedDistId);

  dispatch(clearNetworkError(false));
  dispatch(set_first_all_distributions_load_status('success'));
  return true;
};

const mergeDistributionsResult = (state, incoming) => {
  const existing = state.distribution_data.distributions?.distributions || {};
  const incomingMap = incoming.distributions || {};
  // Shallow-merge already-parsed distribution trees. Re-running
  // GeneralClasses.Distributions over in-memory trees used to corrupt kc_table
  // month values (API flat data.jan vs client data.byMonth.jan).
  const merged = new GeneralClasses.Distributions({});
  merged.distributions = { ...existing, ...incomingMap };
  return merged;
};

// Smart skeleton merge. The skeleton is the source of truth for structure and
// light fields (name, role) and for membership: new distributions / farming
// units are added, ones that disappeared are pruned, and a changed name/role is
// taken from the skeleton. Already-loaded heavy detail (farming-unit fields,
// sensors, historical_data, and any other distribution attributes) is preserved
// — the skeleton never clobbers a full tree.
const mergeSkeletonIntoDistributions = (existingMap, skeletonList) => {
  const result = {};

  for (const skel of skeletonList) {
    if (!skel?.id) {
      continue;
    }
    const prev = existingMap[skel.id];
    const skelFus = skel.farming_units || {};

    if (!prev) {
      // Brand-new distribution — skeleton only until it is selected/loaded.
      result[skel.id] = { ...skel };
      continue;
    }

    // A tree that was never hydrated holds only skeleton detail, so the fresh
    // skeleton is strictly better than what we have — including its fields.
    // Preserving the old slim fields there would hide fields added since the
    // last skeleton on any distribution the user has not opened yet.
    const prevIsSkeleton = prev.is_skeleton === true;

    const prevFus = prev.farming_units || {};
    const mergedFus = {};
    for (const fuId in skelFus) {
      const prevFu = prevFus[fuId];
      if (!prevFu || prevIsSkeleton) {
        // New farming unit, or one we only ever had skeleton detail for.
        mergedFus[fuId] = { ...skelFus[fuId] };
      } else {
        // Keep loaded detail (fields, sensors, historical_data); take the new
        // light fields (name, role) from the skeleton.
        mergedFus[fuId] = {
          ...prevFu,
          name: skelFus[fuId].name,
          role: skelFus[fuId].role,
        };
      }
    }
    // Farming units present before but absent from the skeleton are pruned.

    result[skel.id] = {
      ...prev,
      name: skel.name,
      role: skel.role,
      // Hydrated trees stay hydrated: the skeleton never re-flags them.
      is_skeleton: prevIsSkeleton,
      farming_units: mergedFus,
    };
  }
  // Distributions present before but absent from the skeleton are pruned.

  return result;
};

// Fields the user can actually work with in the current mode. Mirrors the field
// resolution below so "this farming unit has fields" means the same thing in
// both places.
const hasSelectableFields = (farming_unit, is_archive_mode) => {
  const fields = farming_unit?.fields;
  if (!fields) {
    return false;
  }
  const wanted = is_archive_mode ? 'archive' : 'active';
  return Object.values(fields).some(
    field => field?.settings?.field_state === wanted
  );
};

// Pick the farming unit to land on when nothing was previously selected.
// Landing on a farming unit with no fields shows an empty screen the user has
// to click out of, so prefer the first one that actually has fields; fall back
// to the first farming unit when none do (an empty distribution still needs a
// selection). Skeleton farming units carry no fields, so this only resolves
// once the full tree lands — which for the initial distribution is the
// parallel GET distribution/{id} at t=0.
const pickDefaultFarmingUnit = (farming_units, is_archive_mode) => {
  const units = Object.values(farming_units || {});
  return (
    units.find(fu => hasSelectableFields(fu, is_archive_mode)) ||
    units[0] ||
    undefined
  );
};

// Persist only hydrated full trees — the same content DISTRIBUTIONS_DATA held
// before the skeleton-first load. Skeleton stubs are never written: painting
// them on cold start would look like data and then block re-hydration.
//
// Union with the previous cache so a persist that only holds the selected tree
// (startup, before background backfill) does not wipe full trees hydrated in
// an earlier session or on a prior switch.
const persistDistributionsCache = async (state, currentId) => {
  const distributions =
    state.distribution_data.distributions?.distributions || {};
  const knownIds = new Set(Object.keys(distributions));
  const fullTrees = {};

  const previous = parseDistributionsCache(
    await getItem(DISTRIBUTIONS_CACHE_KEY)
  );
  if (previous?.distributions) {
    for (const id in previous.distributions) {
      if (
        knownIds.has(id) &&
        distributionManager.isFullDistributionTree(previous.distributions[id])
      ) {
        fullTrees[id] = previous.distributions[id];
      }
    }
  }

  for (const id in distributions) {
    if (distributionManager.isFullDistributionTree(distributions[id])) {
      fullTrees[id] = distributions[id];
    }
  }

  if (!Object.keys(fullTrees).length) {
    return;
  }

  await setItem(
    DISTRIBUTIONS_CACHE_KEY,
    stringifyDistributionsCache({
      version: 2,
      savedAt: Date.now(),
      distributions: fullTrees,
      currentId:
        currentId ||
        state.distribution_data.selected_entities.distribution?.id ||
        deriveIndexFromDistributions(fullTrees)[0]?.id,
    })
  );
};

// Fire-and-forget persist so hydrate paths never block on IndexedDB writes.
const schedulePersistDistributionsCache = (getState, currentId) => {
  setImmediate(async () => {
    try {
      await persistDistributionsCache(getState(), currentId);
    } catch (e) {
      console.error('Failed to cache distributions:', e);
    }
  });
};

// Drop the persisted distributions cache and the in-memory tree map. Units are
// baked into the tree server-side, so on a unit-system change both the
// IndexedDB cache and the Redux map hold trees in the old units and must be
// discarded before the fresh (new-unit) load. Awaiting the removeItem before
// re-loading matters: an in-flight load's setImmediate(persistDistributionsCache)
// could otherwise rewrite the cache after we deleted it.
export var clear_distributions_cache = () => async dispatch => {
  await removeItem(DISTRIBUTIONS_CACHE_KEY);
  distributionsFreshAt.clear();
  dispatch(set_all_distributions(null));
};

// Helper function to check if farming unit history is already loading
const isFarmingUnitHistoryLoading = (getState, farmingUnitId) => {
  const farmingUnitLoadingObject =
    getState().distribution_data.farming_unit_load_history;
  return (
    farmingUnitLoadingObject.status === 'init' &&
    farmingUnitLoadingObject.farming_unit_ids.includes(farmingUnitId)
  );
};

// Helper function to dispatch history loads
const dispatchHistoryLoads = (
  dispatch,
  distributionId,
  farmingUnitId,
  fieldId = null
) => {
  dispatch(get_farming_units_history(distributionId, farmingUnitId, null));
  setTimeout(() => {
    dispatch(get_farming_units_recommendations(distributionId, farmingUnitId));
  }, 0);
  if (fieldId) {
    dispatch(get_field_history(distributionId, farmingUnitId, fieldId));
  }
};

// Helper function to load history if not already loading
const loadHistoryIfNeeded = (
  dispatch,
  getState,
  distributionId,
  farmingUnitId,
  fieldId = null
) => {
  if (!isFarmingUnitHistoryLoading(getState, farmingUnitId)) {
    dispatchHistoryLoads(dispatch, distributionId, farmingUnitId, fieldId);
  }
};

// Fire-and-forget full-tree refresh for the selected distribution (master-style:
// paginated .then). Does not block load_distributions or the UI shell.
// Returns the in-flight promise so callers can delay ensure_all until it
// settles — keeps selected by-id first on the connection pool while testing.
const refreshSelectedDistributionInBackground = (
  dispatch,
  getState,
  requestId,
  selectedDistId
) => {
  if (!selectedDistId) {
    return Promise.resolve();
  }

  return distributionManager.getDistributionById(selectedDistId).then(
    tree => {
      if (
        !mergeSelectedDistributionTree(
          dispatch,
          getState,
          requestId,
          selectedDistId,
          tree
        )
      ) {
        return;
      }
    },
    err => {
      if (requestId !== lastLoadDistributionsRequestId) {
        return;
      }
      deferHistoryDuringInitialTreeLoad = false;
      console.log(err);
      console.error(
        '[Distributions] selected distribution load error:',
        err.message
      );
    }
  );
};

// Run ensure_all after selected by-id settles (or immediately when none).
const scheduleEnsureAllAfterSelected = (
  dispatch,
  requestId,
  selectedTreePromise,
  { forceRevalidate = false } = {}
) => {
  const run = () => {
    if (requestId !== lastLoadDistributionsRequestId) {
      return;
    }
    dispatch(ensure_all_distributions_loaded({ forceRevalidate })).catch(
      err => {
        console.error(
          '[Distributions] cache backfill failed:',
          err?.message || err
        );
      }
    );
  };

  if (selectedTreePromise) {
    Promise.resolve(selectedTreePromise).then(run, run);
  } else {
    setTimeout(run, 0);
  }
};

// Middleware
export var load_distributions = (
  force_load = false,
  shouldUseCache = false,
  selectedDistributionId = null,
  selectedFarmingUnitId = null,
  selectedFieldId = null
) => {
  return async (dispatch, getState) => {
    let state = getState();
    const requestId = ++lastLoadDistributionsRequestId;

    // Load only once (unless forced)
    if (state.distribution_data.distributions && !force_load) {
      return;
    }

    dispatch(set_first_all_distributions_load_status('init'));
    dispatch(set_selected_distribution_network_refresh_status('pending'));
    deferHistoryDuringInitialTreeLoad = true;

    // t=0 — skeleton, cache read, and GET distribution/{id} all start together
    // (same pattern as the old summary path: no await-before-fetch chain).
    const cacheReadPromise = shouldUseCache
      ? getItem(DISTRIBUTIONS_CACHE_KEY)
      : Promise.resolve(null);

    const skeletonPromise = distributionManager
      .getDistributionsSkeleton()
      .catch(err => {
        console.log(err);
        console.error('[Distributions] skeleton error:', err.message);
        return null;
      });

    const selectedDistIdPromise = resolveSelectedDistIdForFetch(
      getState,
      selectedDistributionId,
      cacheReadPromise,
      shouldUseCache
    );

    const selectedTreePromise = selectedDistIdPromise.then(async distId => {
      if (!distId) {
        return { distId: null, tree: null, skipNetwork: false };
      }

      // Gate by-id on cache freshness so a fresh IndexedDB tree does not pay
      // for GET /distribution/{id} on every reload. Stale → paint cache, refresh
      // in background (see backgroundRefreshStarted below).
      if (shouldUseCache) {
        const earlyCache = parseDistributionsCache(await cacheReadPromise);
        if (earlyCache) {
          seedFreshAtFromCache(earlyCache);
          const cachedRaw = earlyCache.distributions?.[distId];
          if (cachedRaw) {
            const cachedTree = new GeneralClasses.Distributions({
              [distId]: cachedRaw,
            }).distributions[distId];
            if (distributionManager.isFullDistributionTree(cachedTree)) {
              if (!isFullDistributionTreeStale(distId)) {
                return { distId, tree: null, skipNetwork: true };
              }
              // Stale full tree: skip blocking by-id; SWR runs after paint.
              return { distId, tree: null, skipNetwork: false };
            }
          }
        }
      }

      try {
        const tree = await distributionManager.getDistributionById(distId);
        return { distId, tree, skipNetwork: false };
      } catch (err) {
        console.log(err);
        console.error(
          '[Distributions] selected distribution load error:',
          err.message
        );
        return { distId, tree: null, skipNetwork: false };
      }
    });

    let cache = null;
    let usableScreen = false;

    const cachePaintPromise = (async () => {
      if (!shouldUseCache) {
        return;
      }
      cache = parseDistributionsCache(await cacheReadPromise);
      if (!cache) {
        return;
      }
      seedFreshAtFromCache(cache);
      dispatch(
        set_all_distributions(
          new GeneralClasses.Distributions(cache.distributions)
        )
      );
      dispatch(distributionActions.selected_entities(null, null, null, null));
      state = getState();
      const se = state.distribution_data.selected_entities;
      if (se?.distribution?.id && se?.farming_unit?.id) {
        usableScreen = true;
        dispatch(set_first_all_distributions_load_status('success'));
      }
    })();

    const [skeleton, treeResult, , currentId] = await Promise.all([
      skeletonPromise,
      selectedTreePromise,
      cachePaintPromise,
      resolveCurrentDistributionId(getState),
    ]);

    if (requestId !== lastLoadDistributionsRequestId) {
      return;
    }

    const {
      distId: fetchedDistId,
      tree: selectedTree,
      skipNetwork: skippedFreshNetwork,
    } = treeResult;

    if (skeleton && skeleton.length) {
      state = getState();
      const existingMap =
        state.distribution_data.distributions?.distributions || {};
      const mergedMap = mergeSkeletonIntoDistributions(existingMap, skeleton);
      dispatch(
        set_all_distributions(new GeneralClasses.Distributions(mergedMap))
      );
    }

    state = getState();
    const distributionsMap =
      state.distribution_data.distributions?.distributions || {};
    const selectedDistId = deriveSelectedDistIdFromState(
      state,
      selectedDistributionId,
      cache,
      currentId
    );

    let treeMerged = false;
    if (selectedTree && fetchedDistId) {
      treeMerged = mergeSelectedDistributionTree(
        dispatch,
        getState,
        requestId,
        fetchedDistId,
        selectedTree
      );
    }

    if (!usableScreen && skeleton && skeleton.length) {
      usableScreen = true;
    }
    if (!usableScreen && Object.keys(distributionsMap).length) {
      usableScreen = true;
    }

    state = getState();
    if (state.distribution_data.distributions?.distributions) {
      postDistributionLoadAnalytics(state.distribution_data.distributions);
    }

    if (usableScreen && !treeMerged) {
      dispatch(clearNetworkError(false));
      dispatch(set_first_all_distributions_load_status('success'));
    } else if (
      !usableScreen &&
      !skeleton &&
      !Object.keys(distributionsMap).length
    ) {
      deferHistoryDuringInitialTreeLoad = false;
      dispatch(set_first_all_distributions_load_status('fail'));
      dispatch(set_selected_distribution_network_refresh_status('fail'));
    }

    // Fresh cache: skip selected by-id. Missing selected tree: background
    // by-id. Stale full tree already painted: ensure_all below does bulk SWR
    // (no single-dist GET). Delay ensure_all until selected by-id finishes
    // so by-id gets the pool first (testing).
    let selectedRefreshPromise = null;
    if (skippedFreshNetwork) {
      deferHistoryDuringInitialTreeLoad = false;
      dispatch(clearNetworkError(false));
      dispatch(set_selected_distribution_network_refresh_status('success'));
    } else {
      state = getState();
      const selectedInState =
        state.distribution_data.distributions?.distributions?.[selectedDistId];
      const selectedAlreadyFull =
        !!selectedDistId &&
        distributionManager.isFullDistributionTree(selectedInState);

      if (
        !!selectedDistId &&
        !selectedAlreadyFull &&
        (!treeMerged || selectedDistId !== fetchedDistId)
      ) {
        selectedRefreshPromise = refreshSelectedDistributionInBackground(
          dispatch,
          getState,
          requestId,
          selectedDistId
        );
      } else if (!treeMerged && !selectedAlreadyFull) {
        deferHistoryDuringInitialTreeLoad = false;
        dispatch(set_selected_distribution_network_refresh_status('fail'));
      } else if (selectedAlreadyFull) {
        deferHistoryDuringInitialTreeLoad = false;
        dispatch(clearNetworkError(false));
        dispatch(set_selected_distribution_network_refresh_status('success'));
      }
    }

    // Post-paint ensure_all: backfill skeletons + TTL-stale trees. Forced
    // refresh of still-fresh trees is gated by
    // FORCE_REVALIDATE_DISTRIBUTIONS_ON_RELOAD (off by default).
    if (usableScreen || treeMerged || selectedRefreshPromise) {
      scheduleEnsureAllAfterSelected(
        dispatch,
        requestId,
        selectedRefreshPromise,
        { forceRevalidate: FORCE_REVALIDATE_DISTRIBUTIONS_ON_RELOAD }
      );
    }
  };
};

export var load_distribution_by_id = (distributionId, options = {}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const existing =
      state.distribution_data.distributions?.distributions?.[distributionId];
    // A skeleton entry already has farming_units (id/name/role only), so the
    // presence of farming_units is not enough — only skip the fetch when the
    // full tree (fields on every farming unit) is already loaded.
    const isFull = distributionManager.isFullDistributionTree(existing);
    const forceNetwork = options.forceNetwork === true;

    if (isFull && !forceNetwork) {
      // Stale-while-revalidate: keep serving the tree; refresh every full tree
      // via ensure_all (distributions(1,5)), not a single by-id GET.
      if (isFullDistributionTreeStale(distributionId)) {
        dispatch(ensure_all_distributions_loaded()).catch(() => {});
      }
      return existing;
    }

    let loadPromise = pendingDistributionTreeLoads[distributionId];
    if (!loadPromise) {
      const controller =
        typeof AbortController !== 'undefined' ? new AbortController() : null;
      if (controller) {
        pendingDistributionTreeAbortControllers[distributionId] = controller;
      }

      loadPromise = (async () => {
        try {
          const tree = await distributionManager.getDistributionById(
            distributionId,
            controller ? { signal: controller.signal } : {}
          );
          const already =
            getState().distribution_data.distributions?.distributions?.[
              distributionId
            ];
          // Bulk may have landed this tree first and aborted us — keep that
          // result. forceNetwork (ensure_all fallback) may also replace a full
          // tree; preserve historical_data either way.
          if (distributionManager.isFullDistributionTree(already)) {
            if (controller?.signal.aborted && !forceNetwork) {
              return already;
            }
            const withHistory = preserveHistoricalData(already, tree);
            const merged = mergeDistributionsResult(getState(), {
              distributions: {
                [distributionId]: withHistory,
              },
            });
            dispatch(set_all_distributions(merged));
            markDistributionTreeFresh(distributionId);
            schedulePersistDistributionsCache(getState, distributionId);
            return merged.distributions[distributionId];
          }
          const merged = mergeDistributionsResult(getState(), {
            distributions: {
              [distributionId]: tree,
            },
          });
          dispatch(set_all_distributions(merged));
          markDistributionTreeFresh(distributionId);
          // Same as pre-skeleton-first: every hydrated full tree lands in
          // DISTRIBUTIONS_DATA so cold relaunch after a switch paints instantly.
          schedulePersistDistributionsCache(getState, distributionId);
          return merged.distributions[distributionId];
        } catch (err) {
          if (isRequestAborted(err)) {
            const fromBulk =
              getState().distribution_data.distributions?.distributions?.[
                distributionId
              ];
            if (distributionManager.isFullDistributionTree(fromBulk)) {
              return fromBulk;
            }
            return null;
          }
          throw err;
        } finally {
          if (pendingDistributionTreeLoads[distributionId] === loadPromise) {
            delete pendingDistributionTreeLoads[distributionId];
          }
          if (
            pendingDistributionTreeAbortControllers[distributionId] ===
            controller
          ) {
            delete pendingDistributionTreeAbortControllers[distributionId];
          }
        }
      })();
      pendingDistributionTreeLoads[distributionId] = loadPromise;
    }

    return loadPromise;
  };
};

// Cross-distribution screens (technician assistant, transmitter search, remote
// configuration modal) need every distribution's full field tree, not just the
// selected one — they resolve fields and sensors by id across all customers.
// With the skeleton-first load only the selected distribution is hydrated, so
// these screens call this once on open (and startup cache backfill calls it
// after first paint) to fill in the rest via the legacy paginated route.
//
// Always uses distributions(1, 5) — paginated bulk with one connection-pool
// slot left free for selected by-id / history / other traffic (was 6). Not N
// per-id GETs. Fills skeleton/missing trees and revalidates full trees whose
// freshAt is older than DISTRIBUTIONS_CACHE_STALE_MS (client-only; no API
// change). Pass { forceRevalidate: true } to refresh every full tree even when
// still inside TTL (startup reload: paint cache, then background bulk).
// load_distribution_by_id kicks ensure_all for stale full trees instead of a
// single by-id GET. When replacing full trees from includeStations=false bulk,
// preserveDistributionClientData keeps historical_data and
// geo_data.meteorological_sources from the previous tree (Update Field / graphs).
// If a user switched to a cold dist and kicked a lazy per-id fetch, bulk aborts
// that per-id when it lands the same tree first; we never abort the bulk the
// other way (pages batch many dists).
export var ensure_all_distributions_loaded = (options = {}) => {
  return (dispatch, getState) => {
    const forceRevalidate = options.forceRevalidate === true;

    if (pendingEnsureAllPromise) {
      // Non-force can join an in-flight pass (force is a superset). A later
      // force must not be dropped by an in-flight non-force — chain after.
      if (forceRevalidate && !pendingEnsureAllForce) {
        return pendingEnsureAllPromise.then(() =>
          dispatch(ensure_all_distributions_loaded({ forceRevalidate: true }))
        );
      }
      return pendingEnsureAllPromise;
    }

    pendingEnsureAllForce = forceRevalidate;

    pendingEnsureAllPromise = (async () => {
      const state = getState();
      const distributionsMap =
        state.distribution_data.distributions?.distributions || {};
      const idsMissing = Object.keys(distributionsMap).filter(
        id => !distributionManager.isFullDistributionTree(distributionsMap[id])
      );
      // Cached full trees that never get load_distribution_by_id would stay
      // stale forever — revalidate them here on the same bulk pass. Startup
      // may pass forceRevalidate via FORCE_REVALIDATE_DISTRIBUTIONS_ON_RELOAD.
      const idsStale = Object.keys(distributionsMap).filter(id => {
        if (!distributionManager.isFullDistributionTree(distributionsMap[id])) {
          return false;
        }
        return forceRevalidate || isFullDistributionTreeStale(id);
      });

      if (!idsMissing.length && !idsStale.length) {
        return;
      }

      const idsMissingSet = new Set(idsMissing);
      const idsStaleSet = new Set(idsStale);

      try {
        // parallelPages=5 (not 6): leave one client connection-pool slot for
        // by-id / history while this backfill runs. includeStations false —
        // skip met enrichment on the wire; preserveDistributionClientData
        // keeps prior meteorological_sources / historical_data on replace.
        const bulk = await distributionManager.distributions(1, 5, false);
        const bulkMap = bulk?.distributions || {};

        // Re-read after the await: a lazy per-id switch may have finished first.
        const latestMap =
          getState().distribution_data.distributions?.distributions || {};
        const toMerge = {};

        for (const id of idsMissingSet) {
          if (
            bulkMap[id] &&
            !distributionManager.isFullDistributionTree(latestMap[id])
          ) {
            toMerge[id] = bulkMap[id];
          }
        }

        for (const id of idsStaleSet) {
          if (!bulkMap[id]) {
            continue;
          }
          if (distributionManager.isFullDistributionTree(latestMap[id])) {
            // Non-forced: skip if a concurrent by-id already refreshed this id.
            if (!forceRevalidate && !isFullDistributionTreeStale(id)) {
              continue;
            }
            toMerge[id] = preserveHistoricalData(latestMap[id], bulkMap[id]);
          } else {
            toMerge[id] = bulkMap[id];
          }
        }

        if (Object.keys(toMerge).length) {
          dispatch(
            set_all_distributions(
              mergeDistributionsResult(getState(), {
                distributions: toMerge,
              })
            )
          );
          Object.keys(toMerge).forEach(id => {
            markDistributionTreeFresh(id);
            // Only abort in-flight per-id for ids that were still missing —
            // stale revalidation must not cancel a user-driven switch fetch
            // that already completed into a fresh tree (handled above).
            if (idsMissingSet.has(id)) {
              abortPendingDistributionTreeLoad(id);
            }
          });
          schedulePersistDistributionsCache(getState);
        }
      } catch (err) {
        console.error('[Distributions] bulk load failed:', err.message);
        // Bulk failed — fall back to per-id so screens still work rather than
        // silently showing nothing. Connection-pool cost only on this error path.
        const stillNeeded = [...idsMissingSet, ...idsStaleSet].filter(id => {
          const current =
            getState().distribution_data.distributions?.distributions?.[id];
          if (!distributionManager.isFullDistributionTree(current)) {
            return true;
          }
          return forceRevalidate || isFullDistributionTreeStale(id);
        });
        await Promise.all(
          stillNeeded.map(id =>
            dispatch(load_distribution_by_id(id, { forceNetwork: true }))
          )
        );
      }
    })().finally(() => {
      pendingEnsureAllPromise = null;
      pendingEnsureAllForce = false;
    });

    return pendingEnsureAllPromise;
  };
};

export var selected_entities = (distribution, farming_unit, field, sensors) => {
  return async (dispatch, getState) => {
    var state = getState();
    let is_archive_mode = state.distribution_data.field_state === 'archive';

    // Check if URL params are present - if so, skip localStorage selection and url params will be used
    if (typeof window !== 'undefined' && window.location) {
      const urlParams = new URLSearchParams(window.location.search);
      const hasUrlParams =
        urlParams.has('distributionId') && urlParams.has('farmingUnitId');
      if (hasUrlParams && !distribution && !farming_unit && !field) {
        // URL params exist and no explicit entities passed - skip localStorage loading
        // selected_entities_by_id will be called shortly with the correct entities
        return;
      }
    }

    // Only await AsyncStorage if we need the cached values
    // We only need cached values if there's no current Redux state AND no explicit parameters
    let cachedDistributionId = null;
    let cachedFarmingUnitId = null;
    let cachedFieldId = null;
    let cachedSensorIds = null;

    const hasCurrentState =
      state.distribution_data.selected_entities.distribution ||
      state.distribution_data.selected_entities.farming_unit ||
      state.distribution_data.selected_entities.field;

    const hasExplicitParams = distribution || farming_unit || field || sensors;

    // Only block on AsyncStorage if we have no current state and no explicit parameters
    // (i.e., app startup or first load)
    const needsCachedValues = !hasCurrentState && !hasExplicitParams;

    if (needsCachedValues) {
      if (state.distribution_data.pending_push_selection) {
        return;
      }

      // Only block on AsyncStorage if we actually need the cached values
      [
        cachedDistributionId,
        cachedFarmingUnitId,
        cachedFieldId,
        cachedSensorIds,
      ] = await Promise.all([
        getItem('SELECTED_DISTRIBUTION_ID', null),
        getItem('SELECTED_FARMING_UNIT_ID', null),
        getItem('SELECTED_FIELD_ID', null),
        getItem('SELECTED_SENSOR_TRANSMITTER_IDS', null),
      ]);

      state = getState();
      if (state.distribution_data.pending_push_selection) {
        return;
      }
      // Note: History loading is deferred until after entities are validated and set
      // to avoid race conditions. See shouldLoadSelectedFarmingUnitData logic below.
    }

    // Distribution
    var selected_distribution = distribution;

    if (!selected_distribution) {
      var selected_distribution_id = undefined;

      if (state.distribution_data.selected_entities.distribution) {
        selected_distribution_id =
          state.distribution_data.selected_entities.distribution.id;
      } else if (cachedDistributionId != null) {
        selected_distribution_id = cachedDistributionId;
      }
      if (state.distribution_data?.distributions?.distributions === undefined) {
        selected_distribution = null;
      } else if (
        selected_distribution_id in
        state.distribution_data?.distributions?.distributions
      ) {
        selected_distribution =
          state.distribution_data.distributions.distributions[
            selected_distribution_id
          ];
      } else {
        for (var first_distribution_id in state.distribution_data.distributions
          .distributions)
          break; // Take the first distribution
        selected_distribution =
          state.distribution_data.distributions.distributions[
            first_distribution_id
          ];
      }

      if (
        !selected_distribution &&
        state.distribution_data?.distributions?.distributions
      ) {
        return;
      }
    }

    // Farming Unit
    var selected_farming_unit = farming_unit;

    if (!selected_farming_unit && selected_distribution) {
      var selected_farming_unit_id = undefined;

      if (state.distribution_data.selected_entities.farming_unit) {
        selected_farming_unit_id =
          state.distribution_data.selected_entities.farming_unit.id;
      } else if (cachedFarmingUnitId != null) {
        selected_farming_unit_id = cachedFarmingUnitId;
      } else {
        // Deep links / by-id may have written the intended FU while selection
        // only held a skeleton (or null FU). Read it so the full-tree reselect
        // can finish resolving after GET distribution/{id}.
        selected_farming_unit_id = await getItem(
          'SELECTED_FARMING_UNIT_ID',
          null
        );
      }

      if (
        selected_farming_unit_id &&
        selected_farming_unit_id in selected_distribution.farming_units
      ) {
        selected_farming_unit =
          selected_distribution.farming_units[selected_farming_unit_id];
      } else {
        selected_farming_unit = pickDefaultFarmingUnit(
          selected_distribution.farming_units,
          is_archive_mode
        );
      }
    }

    // Never commit an unhydrated FU into selected_entities — the same rule as
    // optimistic distribution switch. Keep the intended id in storage; the next
    // full-tree selected_entities call resolves it.
    if (
      selected_farming_unit &&
      (!selected_farming_unit.fields ||
        selected_distribution?.is_skeleton === true)
    ) {
      if (selected_farming_unit.id) {
        setItem('SELECTED_FARMING_UNIT_ID', selected_farming_unit.id);
      }
      selected_farming_unit = null;
    }

    // Field
    var selected_field = field;

    // A skeleton farming unit (from the structural skeleton load, not yet
    // hydrated with a full distribution tree) has no `fields` key at all.
    // Treat that as "not ready to resolve a field" rather than crashing on
    // Object.entries(undefined) — the field resolves once the full tree lands.
    if (
      !selected_field &&
      selected_farming_unit &&
      selected_farming_unit.fields
    ) {
      var selected_field_id = undefined;

      if (state.distribution_data.selected_entities.field) {
        selected_field_id = state.distribution_data.selected_entities.field.id;
      } else if (cachedFieldId != null) {
        selected_field_id = cachedFieldId;
      } else {
        // Monday / URL deep links persist the field id before the tree is
        // full; pick it up on the post-hydration reselect.
        selected_field_id = await getItem('SELECTED_FIELD_ID', null);
      }

      // filter just active fields from farmingUnit fields ( field_state is active )
      let filtered_ids = Object.fromEntries(
        Object.entries(selected_farming_unit.fields).filter(
          ([k, field]) => field.settings.field_state === 'active'
        )
      );

      if (is_archive_mode) {
        // when we toggle to archive mode , filter to archive fields only !
        filtered_ids = Object.fromEntries(
          Object.entries(selected_farming_unit.fields).filter(
            ([k, field]) => field.settings.field_state === 'archive'
          )
        );
      }

      if (selected_field_id && selected_field_id in filtered_ids) {
        selected_field = selected_farming_unit.fields[selected_field_id];
      } else {
        for (var first_field_id in filtered_ids) break; // Take the first field
        selected_field = selected_farming_unit.fields[first_field_id];
      }
    }

    let shouldLoadSelectedFarmingUnitData = false;
    let shouldLoadSelectedFieldData = false;
    const currentRecommendations =
      getState().distribution_data.farming_unit_recommendations.recommendations;
    const userLengthUnits = getState().authentication_data.user_data.length;
    const fieldRecommendation =
      selected_field &&
      currentRecommendations &&
      currentRecommendations[selected_field.id];
    const hasRecommendationsForField =
      fieldRecommendation && fieldRecommendation.units === userLengthUnits;
    if (
      (selected_farming_unit &&
        !isFarmingUnitHistoryLoading(getState, selected_farming_unit.id)) ||
      (isSupportingPlannerByWeeks(selected_field) &&
        !hasRecommendationsForField)
    ) {
      // and no history for this field
      // load field history
      shouldLoadSelectedFarmingUnitData = true;
    }

    // Defer history loading to avoid blocking navigation
    // Use setTimeout to make history loading non-blocking
    if (
      shouldLoadSelectedFarmingUnitData &&
      selected_distribution &&
      selected_farming_unit &&
      !deferHistoryDuringInitialTreeLoad
    ) {
      const distId = selected_distribution.id;
      const fuId = selected_farming_unit.id;
      setTimeout(() => {
        dispatchHistoryLoads(dispatch, distId, fuId);
      }, 0);
      shouldLoadSelectedFieldData = true;
    }

    // Optimized: Compare by ID instead of expensive JSON.stringify
    if (
      selected_field &&
      (!state.distribution_data.selected_entities.field ||
        state.distribution_data.selected_entities.field.id !==
          selected_field.id)
    ) {
      shouldLoadSelectedFieldData = true;

      // Create new sensors object to avoid mutating Redux state
      const updatedSensors = Object.values(selected_field.sensors).reduce(
        (acc, currentSensor) => {
          acc[currentSensor.id] = {
            ...currentSensor,
            state: GeneralClasses.SENSOR_STATES.INIT,
          };
          return acc;
        },
        {}
      );
      selected_field = {
        ...selected_field,
        sensors: updatedSensors,
      };
    }

    if (
      shouldLoadSelectedFieldData &&
      selected_distribution &&
      selected_farming_unit &&
      selected_field
    ) {
      const distId = selected_distribution.id;
      const fuId = selected_farming_unit.id;
      const fId = selected_field.id;
      setTimeout(() => {
        dispatch(get_field_history(distId, fuId, fId));
      }, 0);
    }

    // Sensor
    var selected_sensors = sensors;

    if (!selected_sensors && selected_field) {
      var selected_sensor_transmitter_ids = undefined;

      if (cachedSensorIds != null) {
        selected_sensor_transmitter_ids = cachedSensorIds.split(',');

        let field_transmitter_ids = sensorsManager.get_transmitter_ids(
          selected_field.sensors
        );

        // find the stored sensors objects in the current field instance
        let checkSensorsArray = selected_sensor_transmitter_ids.map(
          function (sensor_id) {
            if (field_transmitter_ids.includes(sensor_id)) {
              let selectedSensor = Object.values(selected_field.sensors).filter(
                a => a.transmitter_id == sensor_id
              );
              return selectedSensor;
            } else {
              return undefined;
            }
          }
        );

        if (checkSensorsArray.includes(undefined)) {
          selected_sensors = {};
        } else {
          selected_sensors = selected_sensor_transmitter_ids.reduce(function (
            map,
            obj
          ) {
            let sensor = Object.values(selected_field.sensors).filter(
              a => a.transmitter_id == obj
            )[0];
            map[sensor.id] = sensor;
            return map;
          }, {});
        }
      } else {
        selected_sensors = {};
      }
    }

    // Don't await setItem - fire and forget to avoid blocking UI
    if (selected_distribution) {
      setItem('SELECTED_DISTRIBUTION_ID', selected_distribution.id);
    }
    if (selected_farming_unit) {
      setItem('SELECTED_FARMING_UNIT_ID', selected_farming_unit.id);
    }
    if (selected_field) {
      setItem('SELECTED_FIELD_ID', selected_field.id);
    }

    let field_transmitter_ids =
      sensorsManager.get_transmitter_ids(selected_sensors);
    if (selected_sensors) {
      setItem('SELECTED_SENSOR_TRANSMITTER_IDS', field_transmitter_ids);
    } else {
      setItem('SELECTED_SENSORS_ID', null);
    }

    var mixpanelInstance = getMixpanelInstance();
    mixpanelInstance.register({
      Distribution: selected_distribution ? selected_distribution.name : null,
      'Distribution Id': selected_distribution
        ? selected_distribution.id
        : null,
      'Farming Unit': selected_farming_unit ? selected_farming_unit.name : null,
      'Farming Unit Id': selected_farming_unit
        ? selected_farming_unit.id
        : null,
      Field: selected_field ? selected_field.name : null,
      'Field Id': selected_field ? selected_field.id : null,
      'Early Season': fieldManager.isFieldEarlyDays(selected_field),
      'Crop Type': selected_field ? selected_field.crop_data.crop_type : null,
      'Soil Type': selected_field ? selected_field.soil_type : null,
      // 'Sensor Ids': selected_sensors ? selected_sensors.map(a => a.id) : null,
    });

    // v3.x API: use getPeople() instead of people
    mixpanelInstance.getPeople().set({
      'Last Distribution': selected_distribution
        ? selected_distribution.name
        : null,
      'Last Farming Unit': selected_farming_unit
        ? selected_farming_unit.name
        : null,
      'Last Field': selected_field ? selected_field.name : null,
    });

    dispatch(
      set_selected_entities(
        selected_distribution,
        selected_farming_unit,
        selected_field,
        selected_sensors
      )
    );
  };
};

export var postDistributionLoadAnalytics = distributions => {
  let mixpanelInstance = getMixpanelInstance();
  let lastInstallationDate = getFirstInstallationDate(
    distributions.distributions
  );
  let formattedInstallationTime = lastInstallationDate.format(
    'YYYY-MM-DD HH:mm:ss'
  );

  // v3.x API: use getPeople() instead of people
  mixpanelInstance.getPeople().set({
    'First Installation Date': formattedInstallationTime,
  });

  let allUserCrops = getFieldCrops(distributions.distributions);
  mixpanelInstance.getPeople().union('All user crops', {
    Crops: allUserCrops,
  });
};

export var selected_entities_by_id = (
  distribution_id,
  farming_unit_id,
  field_id = null
) => {
  return (dispatch, getState) => {
    var state = getState();

    // Persist intended ids immediately so a later full-tree selected_entities
    // reselect (after skeleton-first load) still lands on the deep-linked
    // field — e.g. Monday links with ?distributionId&farmingUnitId&fieldId.
    if (distribution_id) {
      setItem('SELECTED_DISTRIBUTION_ID', distribution_id);
    }
    if (farming_unit_id) {
      setItem('SELECTED_FARMING_UNIT_ID', farming_unit_id);
    }
    if (field_id) {
      setItem('SELECTED_FIELD_ID', field_id);
    }

    var selected_distribution = null;
    var selected_farming_unit = null;
    var selected_field = null;

    var all_distributions = state.distribution_data.distributions.distributions;
    if (all_distributions && distribution_id in all_distributions) {
      selected_distribution = all_distributions[distribution_id];
    }

    if (
      selected_distribution &&
      farming_unit_id in selected_distribution.farming_units
    ) {
      selected_farming_unit =
        selected_distribution.farming_units[farming_unit_id];
    }

    // Skeleton FUs omit `fields` — do not pass them into selected_entities
    // (it would either crash or commit a useless FU). Keep dist-only until
    // the full tree lands; storage ids above finish the resolve then.
    if (!selected_farming_unit?.fields) {
      if (selected_distribution) {
        dispatch(
          set_selected_entities(selected_distribution, null, null, null)
        );
      }
      if (distribution_id) {
        dispatch(load_distribution_by_id(distribution_id));
      }
      return;
    }

    if (field_id && field_id in selected_farming_unit.fields) {
      selected_field = selected_farming_unit.fields[field_id];
    }

    dispatch(
      distributionActions.selected_entities(
        selected_distribution,
        selected_farming_unit,
        selected_field,
        null
      )
    );
  };
};

export var delete_distribution = distribution_id => {
  return (dispatch, getState) => {
    dispatch(initiate_distribution_action('DELETE'));

    if (
      !distributionManager.is_admin(
        getState().distribution_data.distributions.distributions,
        distribution_id,
        null
      )
    ) {
      dispatch(
        distribution_action_fail(
          'DELETE',
          new GeneralClasses.LocalError(
            "You don't have the right permissions to delete this distribution"
          )
        )
      );
    } else {
      // create distribution
      distributionManager.delete_distribution(distribution_id).then(
        function (result) {
          dispatch(distribution_action_success('DELETE'));
          dispatch(load_distributions(true));
        },
        function (err) {
          console.log(err);
          dispatch(distribution_action_fail('DELETE', err));
        }
      );
    }
  };
};

export var update_distribution = (distribution_id, distribution_name) => {
  return (dispatch, getState) => {
    dispatch(initiate_distribution_action('UPDATE'));

    if (
      !distributionManager.is_admin(
        getState().distribution_data.distributions.distributions,
        distribution_id,
        null
      )
    ) {
      dispatch(
        distribution_action_fail(
          'UPDATE',
          new GeneralClasses.LocalError(
            "You don't have the right permissions to update this distribution"
          )
        )
      );
    } else {
      // create distribution
      distributionManager
        .update_distribution(distribution_id, distribution_name)
        .then(
          function (result) {
            dispatch(distribution_action_success('UPDATE'));
            dispatch(load_distributions(true));
          },
          function (err) {
            console.log(err);
            dispatch(distribution_action_fail('UPDATE', err));
          }
        );
    }
  };
};

export var create_distribution = distribution_name => {
  return (dispatch, getState) => {
    dispatch(initiate_distribution_action('CREATE'));

    if (!getState().authentication_data.user_details.is_root) {
      dispatch(
        distribution_action_fail(
          'CREATE',
          new GeneralClasses.LocalError(
            "You don't have the right permissions to create distributions"
          )
        )
      );
    } else {
      // create distribution
      distributionManager.create_distribution(distribution_name).then(
        function (result) {
          dispatch(distribution_action_success('CREATE'));
          dispatch(load_distributions(true));
        },
        function (err) {
          console.log(err);
          dispatch(distribution_action_fail('CREATE', err));
        }
      );
    }
  };
};

export var set_field_state = field_state => {
  return (dispatch, getState) => {
    dispatch(set_field_state_action(field_state));
    dispatch(selected_entities(null, null, null, null));
  };
};

export var initiate_distribution_action = distribution_action_type => {
  return {
    type: 'DISTRIBUTION_ACTION_INIT',
    distribution_action_type: distribution_action_type,
  };
};

export var distribution_action_success = distribution_action_type => {
  return {
    type: 'DISTRIBUTION_ACTION_SUCCESS',
    distribution_action_type: distribution_action_type,
  };
};

export var distribution_action_fail = (distribution_action_type, err) => {
  return {
    type: 'DISTRIBUTION_ACTION_FAIL',
    distribution_action_type: distribution_action_type,
    error_message: error_message(err),
  };
};

export var clear_distribution_action = () => {
  return {
    type: 'CLEAR_DISTRIBUTION_ACTION',
  };
};

export var clear_distribution_data = () => {
  return {
    type: 'CLEAR_DISTRIBUTION_DATA',
  };
};

// ======

export var delete_farming_unit = (distribution_id, farming_unit_id) => {
  return (dispatch, getState) => {
    dispatch(initiate_farming_unit_action('DELETE'));

    if (
      !distributionManager.is_admin(
        getState().distribution_data.distributions.distributions,
        distribution_id,
        farming_unit_id
      )
    ) {
      dispatch(
        farming_unit_action_fail(
          'DELETE',
          new GeneralClasses.LocalError(
            "You don't have the right permissions to delete this farming unit"
          )
        )
      );
    } else {
      // delete farming unit
      farmingUnitManager
        .delete_farming_unit(distribution_id, farming_unit_id)
        .then(
          function (result) {
            dispatch(farming_unit_action_success('DELETE'));
            dispatch(load_distributions(true));
          },
          function (err) {
            console.log(err);
            dispatch(farming_unit_action_fail('DELETE', err));
          }
        );
    }
  };
};

export var update_farming_unit = (distribution_id, farming_unit) => {
  return (dispatch, getState) => {
    dispatch(initiate_farming_unit_action('UPDATE'));

    if (
      !distributionManager.is_admin(
        getState().distribution_data.distributions.distributions,
        distribution_id,
        farming_unit.id
      )
    ) {
      dispatch(
        farming_unit_action_fail(
          'UPDATE',
          new GeneralClasses.LocalError(
            "You don't have the right permissions to update this farming unit"
          )
        )
      );
    } else {
      // update farming unit
      farmingUnitManager
        .update_farming_unit(distribution_id, farming_unit)
        .then(
          function (result) {
            dispatch(farming_unit_action_success('UPDATE'));

            // Optimistically merge the saved farming unit (with its sensors)
            // into Redux immediately, so newly added sensors show right away
            // instead of only after the load_distributions re-pull lands past
            // GSI propagation. The re-pull below reconciles ids (tmp_ -> SC_N).
            dispatch(
              update_farming_unit_optimistic(distribution_id, farming_unit)
            );

            dispatch(load_distributions(true));
          },
          function (err) {
            console.log(err);
            dispatch(farming_unit_action_fail('UPDATE', err));
          }
        );
    }
  };
};

export var create_farming_unit = (distribution_id, farming_unit) => {
  return (dispatch, getState) => {
    dispatch(initiate_farming_unit_action('CREATE'));

    if (
      !distributionManager.is_admin(
        getState().distribution_data.distributions.distributions,
        distribution_id,
        null
      )
    ) {
      dispatch(
        farming_unit_action_fail(
          'CREATE',
          new GeneralClasses.LocalError(
            "You don't have the right permissions to create a farming unit under this distribution"
          )
        )
      );
    } else {
      // create farming unit
      farmingUnitManager
        .create_farming_unit(distribution_id, farming_unit)
        .then(
          function (result) {
            dispatch(farming_unit_action_success('CREATE'));
            dispatch(load_distributions(true));
          },
          function (err) {
            console.log(err);
            dispatch(farming_unit_action_fail('CREATE', err));
          }
        );
    }
  };
};

/**
 * Refresh selected_entities with the latest data from distributions state.
 * Call after updating historical data so components that read from
 * selected_entities see the new data without requiring a field re-selection.
 */
function refreshSelectedEntities(
  dispatch,
  getState,
  distributionId,
  farmingUnitId
) {
  const state = getState();
  const selectedEntities = state.distribution_data.selected_entities;
  if (
    !selectedEntities.farming_unit ||
    selectedEntities.farming_unit.id !== farmingUnitId
  ) {
    return;
  }

  const updatedFarmingUnit =
    state.distribution_data.distributions?.distributions?.[distributionId]
      ?.farming_units?.[farmingUnitId];

  if (!updatedFarmingUnit) return;

  const selectedField = selectedEntities.field;
  const updatedField =
    selectedField && updatedFarmingUnit.fields?.[selectedField.id]
      ? updatedFarmingUnit.fields[selectedField.id]
      : selectedField;

  dispatch(
    set_selected_entities(
      selectedEntities.distribution,
      updatedFarmingUnit,
      updatedField,
      selectedEntities.sensors
    )
  );
}

export var get_farming_units_history = (
  distribution_id,
  farming_unit_id,
  field_id
) => {
  return (dispatch, getState) => {
    if (!farming_unit_id) {
      return Promise.resolve();
    }

    // Share one GET farming_unit/{dist}/{fu} across concurrent callers (cache
    // paint + by-id reselect + AllFarmView mount) so duplicates do not burn
    // HTTP/1.1 pool slots. URL ignores field_id — key by farming unit only.
    let loadPromise = pendingFarmingUnitHistoryLoads[farming_unit_id];
    if (loadPromise) {
      return loadPromise;
    }

    dispatch(initiate_farming_unit_history_load_action(farming_unit_id));

    loadPromise = farmingUnitManager
      .loadFarmingUnitHistory(distribution_id, farming_unit_id, field_id)
      .then(
        function (result) {
          let currentFarmingUnit =
            getState().distribution_data.distributions?.distributions?.[
              distribution_id
            ]?.farming_units?.[farming_unit_id];

          // Guard: If farming unit not found, just clear status and return
          if (!currentFarmingUnit) {
            console.log(
              'get_farming_units_history: farming unit not found in state'
            );
            dispatch(clear_status_farming_unit_history_load_action());
            return;
          }

          dispatch(
            update_farming_unit_historical_data(
              distribution_id,
              farming_unit_id,
              result['historicalData']
            )
          );

          refreshSelectedEntities(
            dispatch,
            getState,
            distribution_id,
            farming_unit_id
          );

          dispatch(success_farming_unit_history_load_action(farming_unit_id));
          dispatch(clear_status_farming_unit_history_load_action());
        },
        function (err) {
          console.log('get_farming_units_history error:', err);
          dispatch(clear_status_farming_unit_history_load_action());
        }
      )
      .finally(() => {
        if (pendingFarmingUnitHistoryLoads[farming_unit_id] === loadPromise) {
          delete pendingFarmingUnitHistoryLoads[farming_unit_id];
        }
      });

    pendingFarmingUnitHistoryLoads[farming_unit_id] = loadPromise;
    return loadPromise;
  };
};

export var get_field_history = (distribution_id, farming_unit_id, field_id) => {
  return (dispatch, getState) => {
    if (!field_id) {
      return Promise.resolve();
    }

    // Share one GET field/{dist}/{fu}/{field} across concurrent callers so
    // startup does not burn HTTP/1.1 pool slots on duplicate history fetches.
    let loadPromise = pendingFieldHistoryLoads[field_id];
    if (loadPromise) {
      return loadPromise;
    }

    dispatch(initiate_field_history_load_action(field_id));

    loadPromise = fieldManager
      .loadFieldHistory(distribution_id, farming_unit_id, field_id)
      .then(
        function (result) {
          const historicalData = result['historicalData'][field_id];

          dispatch(
            update_field_historical_data(
              distribution_id,
              farming_unit_id,
              field_id,
              historicalData
            )
          );

          refreshSelectedEntities(
            dispatch,
            getState,
            distribution_id,
            farming_unit_id
          );

          dispatch(success_field_history_load_action(field_id));
          dispatch(clear_status_field_history_load_action());
        },
        function (err) {
          console.log(err);
          dispatch(clear_status_field_history_load_action());
        }
      )
      .finally(() => {
        if (pendingFieldHistoryLoads[field_id] === loadPromise) {
          delete pendingFieldHistoryLoads[field_id];
        }
      });

    pendingFieldHistoryLoads[field_id] = loadPromise;
    return loadPromise;
  };
};

// ======

export var initiate_farming_unit_action = farming_unit_action_type => {
  return {
    type: 'FARMING_UNIT_ACTION_INIT',
    farming_unit_action_type: farming_unit_action_type,
  };
};

export var farming_unit_action_success = farming_unit_action_type => {
  return {
    type: 'FARMING_UNIT_ACTION_SUCCESS',
    farming_unit_action_type: farming_unit_action_type,
  };
};

export var farming_unit_action_fail = (farming_unit_action_type, err) => {
  return {
    type: 'FARMING_UNIT_ACTION_FAIL',
    farming_unit_action_type: farming_unit_action_type,
    error_message: error_message(err),
  };
};

export var clear_farming_unit_action = () => {
  return {
    type: 'CLEAR_FARMING_UNIT_ACTION',
  };
};

export var initiate_farming_unit_history_load_action = farming_unit_id => {
  return {
    type: 'LOAD_FARMING_UNIT_HISTORY_ACTION_INIT',
    farming_unit_id: farming_unit_id,
  };
};

export var initiate_field_history_load_action = field_id => {
  return {
    type: 'LOAD_FIELD_HISTORY_ACTION_INIT',
    field_id: field_id,
  };
};

export var clear_farming_unit_history_load_action = () => {
  return {
    type: 'LOAD_FARMING_UNIT_HISTORY_ACTION_CLEAR',
    farming_unit_id: '',
  };
};

export var clear_status_farming_unit_history_load_action = () => {
  return {
    type: 'LOAD_FARMING_UNIT_HISTORY_ACTION_CLEAR_STATUS',
  };
};

export var clear_status_field_history_load_action = () => {
  return {
    type: 'LOAD_FIELD_HISTORY_ACTION_CLEAR_STATUS',
  };
};

export var update_field_historical_data = (
  distributionId,
  farmingUnitId,
  fieldId,
  historicalData
) => {
  return {
    type: 'UPDATE_FIELD_HISTORICAL_DATA',
    payload: { distributionId, farmingUnitId, fieldId, historicalData },
  };
};

export var update_farming_unit_historical_data = (
  distributionId,
  farmingUnitId,
  historicalData
) => {
  return {
    type: 'UPDATE_FARMING_UNIT_HISTORICAL_DATA',
    payload: { distributionId, farmingUnitId, historicalData },
  };
};

// Optimistically update field data in Redux immediately after API success,
// before load_distributions completes (which can take ~10 seconds)
export var update_field_optimistic = (
  distributionId,
  farmingUnitId,
  fieldObject
) => {
  return {
    type: 'UPDATE_FIELD_OPTIMISTIC',
    payload: { distributionId, farmingUnitId, fieldObject },
  };
};

export var update_farming_unit_optimistic = (distributionId, farmingUnit) => {
  return {
    type: 'UPDATE_FARMING_UNIT_OPTIMISTIC',
    payload: { distributionId, farmingUnit },
  };
};

export var success_farming_unit_history_load_action = farming_unit_id => {
  return {
    type: 'LOAD_FARMING_UNIT_HISTORY_ACTION_SUCCESS',
    farming_unit_id: farming_unit_id,
  };
};
export var success_field_history_load_action = field_id => {
  return {
    type: 'LOAD_FIELD_HISTORY_ACTION_SUCCESS',
    field_id: field_id,
  };
};

export var delete_field = (distribution_id, farming_unit_id, field_id) => {
  return (dispatch, getState) => {
    dispatch(initiate_field_action('DELETE'));

    if (
      !distributionManager.is_admin(
        getState().distribution_data.distributions.distributions,
        distribution_id,
        farming_unit_id
      )
    ) {
      dispatch(
        field_action_fail(
          'DELETE',
          new GeneralClasses.LocalError(
            "You don't have the right permissions to delete this field"
          )
        )
      );
    } else {
      // delete farming unit
      fieldManager
        .delete_field(distribution_id, farming_unit_id, field_id)
        .then(
          function (result) {
            dispatch(field_action_success('DELETE'));
            dispatch(load_distributions(true));

            var mixpanelInstance = getMixpanelInstance();

            mixpanelInstance.track('Field Delete');
          },
          function (err) {
            console.log(err);
            dispatch(field_action_fail('DELETE', err));
          }
        );
    }
  };
};

export var update_field = (distribution_id, farming_unit_id, field_object) => {
  return (dispatch, getState) => {
    dispatch(initiate_field_action('UPDATE'));

    if (
      !distributionManager.is_admin(
        getState().distribution_data.distributions.distributions,
        distribution_id,
        farming_unit_id
      ) &&
      !farmingUnitManager.is_moderator(
        getState().distribution_data.selected_entities.farming_unit
      )
    ) {
      dispatch(
        field_action_fail(
          'UPDATE',
          new GeneralClasses.LocalError(
            "You don't have the right permissions to update this field"
          )
        )
      );
    } else {
      // update field
      fieldManager
        .update_field(distribution_id, farming_unit_id, field_object)
        .then(
          function (result) {
            dispatch(field_action_success('UPDATE'));

            // Optimistically update Redux state with the field data we just saved,
            // so the user sees the updated data immediately while load_distributions runs
            dispatch(
              update_field_optimistic(
                distribution_id,
                farming_unit_id,
                field_object
              )
            );

            dispatch(load_distributions(true));

            var mixpanelInstance = getMixpanelInstance();
            mixpanelInstance.track('Field Update');
          },
          function (err) {
            console.log(err);
            dispatch(field_action_fail('UPDATE', err));
          }
        );
    }
  };
};

export var create_field = (distribution_id, farming_unit_id, field_object) => {
  return (dispatch, getState) => {
    var state = getState();

    dispatch(initiate_field_action('CREATE'));

    if (
      !distributionManager.is_admin(
        state.distribution_data.distributions.distributions,
        distribution_id,
        farming_unit_id
      )
    ) {
      dispatch(
        field_action_fail(
          'CREATE',
          new GeneralClasses.LocalError(
            "You don't have the right permissions to create a field under this farming unit"
          )
        )
      );
    } else {
      // create field
      fieldManager
        .create_field(distribution_id, farming_unit_id, field_object)
        .then(
          function (result) {
            dispatch(field_action_success('CREATE'));
            dispatch(
              load_distributions(
                true,
                false,
                null,
                null,
                result['fieldId'],
                null
              )
            );

            mixpanel.track('Field Create', {
              Field: result['fieldName'],
              'Field Id': result['fieldId'],
            });
          },
          function (err) {
            console.log(err);
            dispatch(field_action_fail('CREATE', err));
          }
        );
    }
  };
};

export var initiate_field_action = field_action_type => {
  return {
    type: 'FIELD_ACTION_INIT',
    field_action_type: field_action_type,
  };
};

export var field_action_success = field_action_type => {
  return {
    type: 'FIELD_ACTION_SUCCESS',
    field_action_type: field_action_type,
  };
};

export var field_action_fail = (field_action_type, err) => {
  return {
    type: 'FIELD_ACTION_FAIL',
    field_action_type: field_action_type,
    error_message: error_message(err),
  };
};

export var clear_field_action = () => {
  return {
    type: 'CLEAR_FIELD_ACTION',
  };
};

export var set_selected_entities = (
  selected_distribution,
  selected_farming_unit,
  selected_field,
  selected_sensors
) => {
  return {
    type: 'SET_SELECTED_ENTITIES',
    selected_distribution: selected_distribution,
    selected_farming_unit: selected_farming_unit,
    selected_field: selected_field,
    selected_sensors: selected_sensors,
  };
};

export var set_pending_push_selection = pending_push_selection => {
  return {
    type: 'SET_PENDING_PUSH_SELECTION',
    pending_push_selection,
  };
};

export var clear_pending_push_selection = () => {
  return {
    type: 'CLEAR_PENDING_PUSH_SELECTION',
  };
};

export var set_all_distributions = distributions => {
  return {
    type: 'SET_ALL_DISTRIBUTIONS',
    distributions: distributions,
  };
};

export const set_transmitter_coordinates = transmitter_coordinates => {
  return {
    type: 'SET_TRANSMITTER_COORDINATES',
    transmitter_coordinates,
  };
};

export var set_field_state_action = field_state => {
  return {
    type: 'SET_FIELD_STATE',
    field_state: field_state,
  };
};

export var set_first_all_distributions_load_status = status => {
  return {
    type: 'SET_FIRST_ALL_DISTRIBUTIONS_LOAD_STATUS',
    status: status,
  };
};

export var set_selected_distribution_network_refresh_status = status => {
  return {
    type: 'SET_SELECTED_DISTRIBUTION_NETWORK_REFRESH_STATUS',
    status,
  };
};

export const set_selected_farming_unit = selected_farming_unit => {
  return {
    type: 'SET_SELECTED_FARMING_UNIT',
    selected_farming_unit,
  };
};

export const set_selected_distribution = selected_distribution => {
  return {
    type: 'SET_SELECTED_DISTRIBUTION',
    selected_distribution,
  };
};

export const get_farming_units_recommendations = (
  distribution_id,
  farming_unit_id
) => {
  return async (dispatch, getState) => {
    if (!farming_unit_id) {
      return;
    }

    let loadPromise = pendingFarmingUnitRecommendationsLoads[farming_unit_id];
    if (loadPromise) {
      return loadPromise;
    }

    loadPromise = (async () => {
      const state = getState();
      // Guard every hop: the distributions map can be null (e.g. just after
      // clear_distributions_cache on a unit-system change) and the distribution /
      // farming unit may be skeleton-only. Matches get_farming_units_history.
      const currentFarmingUnit =
        state.distribution_data.distributions?.distributions?.[distribution_id]
          ?.farming_units?.[farming_unit_id];
      if (!currentFarmingUnit || !currentFarmingUnit.fields) return;

      const fieldIds = Object.keys(currentFarmingUnit.fields);
      const recommendations = {};

      await Promise.all(
        fieldIds.map(async fieldId => {
          try {
            if (
              currentFarmingUnit.fields[fieldId].settings.planner_settings
                .type === plannerType.WEEKLY_RECOMMENDATION
            ) {
              const fieldRec = await getRecommendation(fieldId);
              recommendations[fieldId] = fieldRec;
            }
          } catch (e) {
            recommendations[fieldId] = null;
          }
        })
      );

      dispatch({
        type: 'FARMING_UNIT_RECOMMENDATIONS_LOADED',
        payload: { farmingUnitId: farming_unit_id, recommendations },
      });
    })().finally(() => {
      if (
        pendingFarmingUnitRecommendationsLoads[farming_unit_id] === loadPromise
      ) {
        delete pendingFarmingUnitRecommendationsLoads[farming_unit_id];
      }
    });

    pendingFarmingUnitRecommendationsLoads[farming_unit_id] = loadPromise;
    return loadPromise;
  };
};

export const update_field_recommendation_show_rate_line = (
  fieldId,
  showRateLine
) => {
  return {
    type: 'UPDATE_FIELD_RECOMMENDATION_SHOW_RATE_LINE',
    payload: { fieldId, showRateLine },
  };
};
